/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { BarChart3, RefreshCw, TrendingUp, Award, Target, Clock, Star, ExternalLink, Trophy, ChevronLeft, ChevronRight, Crown, Medal, Activity, BookmarkPlus, BookmarkCheck } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, LineChart, ReferenceLine } from "recharts";
import { fmtD } from "../lib/date";
import { CallerIdUsage } from "../components/telephony/CallerIdUsage";

const INTENT = [
  { key: "high", zh: "高意向", en: "High", color: "#059669" },
  { key: "mid", zh: "中/敏感", en: "Mid", color: "#2563eb" },
  { key: "low", zh: "低/无效", en: "Low", color: "#f59e0b" },
  { key: "other", zh: "其他", en: "Other", color: "#cbd5e1" },
];
const scoreColor = (s) => (s == null ? "text-slate-400" : s >= 85 ? "text-emerald-600" : s >= 70 ? "text-blue-600" : s >= 55 ? "text-amber-600" : "text-red-600");
const rankMeta = (r) => [
  { icon: Crown, cls: "text-amber-500", bg: "bg-amber-50 border-amber-200" },
  { icon: Medal, cls: "text-slate-400", bg: "bg-slate-50 border-slate-200" },
  { icon: Medal, cls: "text-orange-400", bg: "bg-orange-50 border-orange-200" },
][r - 1] || { icon: Trophy, cls: "text-slate-300", bg: "bg-white border-slate-100" };

export default function CallAnalytics() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  // intent trend
  const [gran, setGran] = useState("week");
  const [trend, setTrend] = useState(null);
  // weekly leaderboard
  const [week, setWeek] = useState(0);
  const [board, setBoard] = useState(null);
  // personal growth curve
  const [growthAgent, setGrowthAgent] = useState("");
  const [growth, setGrowth] = useState(null);
  // golden scripts library (pin awareness)
  const [pinned, setPinned] = useState({ ids: [], canManage: false });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/telephony/analytics", { params: { days } }); setData(d); }
    catch { setData(null); }
    setLoading(false);
  }, [api, days]);
  useEffect(() => { load(); }, [load]);

  const loadTrend = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/intent-trend", { params: { granularity: gran } }); setTrend(d); }
    catch { setTrend(null); }
  }, [api, gran]);
  useEffect(() => { loadTrend(); }, [loadTrend]);

  const loadBoard = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/leaderboard", { params: { week } }); setBoard(d); }
    catch { setBoard(null); }
  }, [api, week]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const loadGrowth = useCallback(async () => {
    try {
      const { data: d } = await api.get("/telephony/score-trend", { params: growthAgent ? { username: growthAgent, weeks: 8 } : { weeks: 8 } });
      setGrowth(d);
    } catch { setGrowth(null); }
  }, [api, growthAgent]);
  useEffect(() => { loadGrowth(); }, [loadGrowth]);

  const loadPinned = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/golden-scripts"); setPinned({ ids: d.pinnedCallIds || [], canManage: !!d.canManage }); }
    catch { setPinned({ ids: [], canManage: false }); }
  }, [api]);
  useEffect(() => { loadPinned(); }, [loadPinned]);

  const pin = async (item) => {
    try {
      await api.post("/telephony/golden-scripts", { callId: item.id, highlight: item.highlight, summary: item.summary, score: item.score, intentTag: item.intentTag, agentName: item.agentName, leadId: item.leadId });
      toast.success(zh ? "已加入金牌话术库" : "Pinned to library");
      loadPinned();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "收藏失败" : "Failed")); }
  };

  const slots = (data?.timeslots || []).map((h) => ({ ...h, label: `${String(h.hour).padStart(2, "0")}:00` }));
  const bestHour = [...(data?.timeslots || [])].filter((h) => h.total >= 2).sort((a, b) => b.answerRate - a.answerRate)[0];
  const trendPeak = [...(trend?.points || [])].sort((a, b) => b.high - a.high)[0];
  const gPts = growth?.points || [];
  const gScored = gPts.filter((p) => p.avgScore != null);
  const gFirst = gScored[0]?.avgScore;
  const gLast = gScored[gScored.length - 1]?.avgScore;
  const gDelta = (gFirst != null && gLast != null) ? gLast - gFirst : null;
  const gAvg = gScored.length ? Math.round(gScored.reduce((s, p) => s + p.avgScore, 0) / gScored.length) : null;

  return (
    <div className="space-y-6" data-testid="call-analytics-page">
      <div className="flex items-center gap-3 flex-wrap">
        <BarChart3 className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "话务分析" : "Call Analytics"}</h1>
        <span className="text-sm text-slate-500">{data?.canSeeAll ? (zh ? "全公司" : "Company-wide") : (zh ? "我的通话" : "My calls")}</span>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {[7, 30, 90].map((d) => (
              <button key={d} data-testid={`analytics-days-${d}`} onClick={() => setDays(d)}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-1.5 text-sm ${days === d ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`)}>
                {zh ? `${d}天` : `${d}d`}
              </button>
            ))}
          </div>
          <button data-testid="analytics-refresh" onClick={() => { load(); loadTrend(); loadBoard(); loadGrowth(); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Intent trend */}
      <section data-testid="analytics-intent-trend" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <TrendingUp className="h-4 w-4 text-emerald-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "高意向通话趋势" : "High-intent call trend"}</h2>
          {trendPeak && trendPeak.high > 0 && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            {zh ? `峰值 ${trendPeak.label} · ${trendPeak.high} 通高意向` : `Peak ${trendPeak.label} · ${trendPeak.high}`}
          </span>}
          <div className="ml-auto inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {[{ k: "week", zh: "按周", en: "Weekly" }, { k: "month", zh: "按月", en: "Monthly" }].map((g) => (
              <button key={g.k} data-testid={`trend-gran-${g.k}`} onClick={() => setGran(g.k)}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-1.5 text-sm ${gran === g.k ? "bg-emerald-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`)}>
                {zh ? g.zh : g.en}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? "直观看销售势头变化（高意向 vs 中意向通话数）" : "Sales momentum over time"}</p>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={trend?.points || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gHigh" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={0.35} /><stop offset="95%" stopColor="#059669" stopOpacity={0} /></linearGradient>
                <linearGradient id="gMid" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.2} /><stop offset="95%" stopColor="#2563eb" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="high" name={zh ? "高意向" : "High"} stroke="#059669" strokeWidth={2} fill="url(#gHigh)" />
              <Area type="monotone" dataKey="mid" name={zh ? "中意向" : "Mid"} stroke="#4F6BFF" strokeWidth={2} fill="url(#gMid)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Weekly leaderboard */}
      <section data-testid="analytics-leaderboard" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Trophy className="h-4 w-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "话术评分周榜" : "Weekly script leaderboard"}</h2>
          <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-2">
            <button data-testid="board-prev-week" onClick={() => setWeek((w) => w + 1)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-xs text-slate-500 min-w-[150px] text-center" data-testid="board-week-range">{board ? `${fmtD(board.weekStart)} ~ ${fmtD(board.weekEnd)}` : "—"}{week === 0 ? (zh ? "（本周）" : " (this wk)") : ""}</span>
            <button data-testid="board-next-week" disabled={week === 0} onClick={() => setWeek((w) => Math.max(0, w - 1))} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`p-1 rounded border border-slate-200 ${week === 0 ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-slate-50"}`)}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? `按平均话术评分排名（≥ ${board?.minCalls ?? 5} 通已评分通话才进榜）` : `Ranked by avg score (≥ ${board?.minCalls ?? 5} scored calls)`}</p>
        {(board?.ranking || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="board-empty">{zh ? "本周暂无评分通话数据" : "No scored calls this week"}</p>
        ) : (
          <div className="space-y-2">
            {board.ranking.map((a) => {
              const rm = a.rank ? rankMeta(a.rank) : null;
              const RankIcon = rm?.icon;
              return (
                <div key={a.username} data-testid={`board-row-${a.username}`} className={`flex items-center gap-3 rounded-lg border p-2.5 ${rm ? rm.bg : "bg-white border-slate-100"}`}>
                  <div className="w-8 shrink-0 flex items-center justify-center">
                    {RankIcon ? <RankIcon className={`h-5 w-5 ${rm.cls}`} /> : <span className="text-xs text-slate-300">—</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.name}
                      {!a.qualified && <span className="ml-2 text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">{zh ? "通话不足未进榜" : "below min"}</span>}
                    </p>
                    <p className="text-[11px] text-slate-400">{a.count} {zh ? "通已评分" : "scored"} · {zh ? "高意向" : "high"} {a.high}</p>
                  </div>
                  <div className="w-16 shrink-0 text-right">
                    <span className={`text-2xl font-extrabold ${scoreColor(a.avgScore)}`} data-testid={`board-score-${a.username}`}>{a.avgScore}</span>
                    <span className="text-[10px] text-slate-400 block -mt-1">{zh ? "均分" : "avg"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Personal growth curve */}
      <section data-testid="analytics-growth" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Activity className="h-4 w-4 text-violet-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "个人成长曲线" : "Personal growth curve"}</h2>
          {growth?.canSeeAll && (growth?.agents || []).length > 0 && (
            <select
              data-testid="growth-agent-select"
              value={growth?.username || ""}
              onChange={(e) => setGrowthAgent(e.target.value)}
              className="ml-1 text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-200">
              {(growth.agents || []).map((a) => (
                <option key={a.username} value={a.username}>{`${a.name}（${a.count}${zh ? "通" : ""}）`}</option>
              ))}
            </select>
          )}
          {!growth?.canSeeAll && growth?.name && <span className="text-sm text-slate-500">{growth.name}</span>}
          <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-2">
            {gAvg != null && <span className="text-xs text-slate-500">{zh ? "近8周均分" : "8-wk avg"} <b className={scoreColor(gAvg)}>{gAvg}</b></span>}
            {gDelta != null && (
              <span data-testid="growth-delta" className={`text-xs font-semibold rounded-full px-2.5 py-1 border ${gDelta > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : gDelta < 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                {gDelta > 0 ? "▲" : gDelta < 0 ? "▼" : "="} {zh ? `较首周 ${gDelta > 0 ? "+" : ""}${gDelta} 分` : `${gDelta > 0 ? "+" : ""}${gDelta} vs first wk`}
              </span>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? "每位销售近 8 周话术平均分的进步轨迹（激励持续提升）" : "Weekly avg script score over the last 8 weeks"}</p>
        {gScored.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="growth-empty">{zh ? "暂无评分通话数据" : "No scored calls yet"}</p>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={gPts} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => [v ?? "—", n]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {gAvg != null && <ReferenceLine y={gAvg} stroke="#7B5CFF" strokeDasharray="4 4" ifOverflow="extendDomain" label={{ value: zh ? "个人均线" : "self avg", fontSize: 10, fill: "#a78bfa", position: "right" }} />}
                <Line type="monotone" dataKey="teamAvg" name={zh ? "团队均值" : "Team avg"} stroke="#cbd5e1" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
                <Line type="monotone" dataKey="avgScore" name={zh ? "个人平均分" : "Avg score"} stroke="#4F6BFF" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Golden scripts (auto top-3 highlights) */}
      <section data-testid="analytics-golden" className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white p-5">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-1">
          <Crown className="h-4 w-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "金牌话术精选" : "Golden scripts"}</h2>
          <span className="text-[11px] text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{zh ? "本周前三名自动入选" : "Auto top-3"}</span>
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? "周榜前三名的最高分通话亮点，自动沉淀为团队金牌话术" : "Best highlights from this week's top 3 agents"}</p>
        {(board?.golden || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="golden-empty">{zh ? "本周暂无入选话术（需前三名有高分亮点通话）" : "No golden scripts yet this week"}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {board.golden.map((g) => {
              const rm = rankMeta(g.rank); const RankIcon = rm.icon;
              return (
                <div key={g.id} data-testid={`golden-${g.id}`} className="rounded-lg border border-amber-200 bg-white p-3 shadow-sm">
                  <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-1.5">
                    <RankIcon className={`h-4 w-4 ${rm.cls}`} />
                    <span className="text-xs font-semibold text-slate-700">{zh ? `第 ${g.rank} 名` : `#${g.rank}`} · {g.agentName}</span>
                    <span className={`ml-auto inline-flex items-center gap-0.5 text-sm font-bold ${scoreColor(g.score)}`}><Star className="h-3.5 w-3.5 fill-current" />{g.score}</span>
                  </div>
                  {g.highlight && <p className="text-sm text-slate-800 border-l-2 border-amber-400 pl-2 mb-1">“{g.highlight}”</p>}
                  {g.summary && <p className="text-xs text-slate-500 line-clamp-2">{g.summary}</p>}
                  <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mt-1.5">
                    {g.leadId && <button onClick={() => navigate(`/crm/leads/${g.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-amber-700 hover:underline inline-flex items-center gap-0.5 text-xs">{g.name || (zh ? "查看客户" : "Lead")}<ExternalLink className="h-3 w-3" /></button>}
                    {pinned.canManage && (
                      pinned.ids.includes(g.id)
                        ? <span data-testid={`golden-pinned-${g.id}`} className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-600"><BookmarkCheck className="h-3.5 w-3.5" />{zh ? "已入库" : "In library"}</span>
                        : <button data-testid={`golden-pin-${g.id}`} onClick={() => pin(g)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 border border-amber-200 rounded px-2 py-1"><BookmarkPlus className="h-3.5 w-3.5" />{zh ? "收藏入库" : "Pin"}</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Caller ID usage */}
      <CallerIdUsage zh={zh} days={days} />

      {/* Time-slot analysis */}
      <section data-testid="analytics-timeslots" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-1">
          <Clock className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "通话时段分析（接通率 & 通话量）" : "Answer rate & volume by hour"}</h2>
          {bestHour && <span className="ml-auto text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            {zh ? `黄金时段 ${String(bestHour.hour).padStart(2, "0")}:00 · 接通率 ${bestHour.answerRate}%` : `Best ${bestHour.hour}:00 · ${bestHour.answerRate}%`}
          </span>}
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? "用于安排班次与集中外呼时间" : "For shift & outbound scheduling"}</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={slots} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="total" name={zh ? "通话量" : "Volume"} fill="#c7d2fe" radius={[3, 3, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="answerRate" name={zh ? "接通率%" : "Answer %"} stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Intent funnel per agent */}
      <section data-testid="analytics-intent" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-3">
          <Target className="h-4 w-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "坐席意向漏斗 & 平均评分" : "Intent funnel & avg score by agent"}</h2>
          <div className="ml-auto flex items-center gap-3 text-xs">
            {INTENT.map((i) => <span key={i.key} className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.color }} />{zh ? i.zh : i.en}</span>)}
          </div>
        </div>
        <div className="space-y-3">
          {(data?.intentByAgent || []).length === 0 && <p className="text-sm text-slate-400 text-center py-6" data-testid="analytics-intent-empty">{zh ? "暂无数据" : "No data"}</p>}
          {(data?.intentByAgent || []).map((a) => (
            <div key={a.username} data-testid={`intent-agent-${a.username}`} className="flex-wrap md:flex-nowrap flex items-center gap-3">
              <div className="w-28 shrink-0">
                <p className="text-sm font-medium text-slate-800 truncate">{a.name}</p>
                <p className="text-xs text-slate-400">{a.total} {zh ? "通" : "calls"}</p>
              </div>
              <div className="flex-1 h-6 rounded-md overflow-hidden flex bg-slate-100" title={`高${a.high}/中${a.mid}/低${a.low}/其他${a.other}`}>
                {INTENT.map((i) => {
                  const pct = a.total ? (a[i.key] / a.total) * 100 : 0;
                  return pct > 0 ? <div key={i.key} style={{ width: `${pct}%`, background: i.color }} className="h-full flex items-center justify-center text-[10px] text-white font-medium">{a[i.key] > 0 && pct > 8 ? a[i.key] : ""}</div> : null;
                })}
              </div>
              <div className="w-20 shrink-0 text-right">
                <span className="text-xs text-slate-400">{zh ? "均分" : "Avg"}</span>{" "}
                <span data-testid={`intent-score-${a.username}`} className={`text-lg font-bold ${scoreColor(a.avgScore)}`}>{a.avgScore ?? "—"}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Top scripts library */}
      <section data-testid="analytics-topscripts" className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-1">
          <Award className="h-4 w-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "Top 话术库（高分通话亮点）" : "Top scripts library"}</h2>
        </div>
        <p className="text-xs text-slate-400 mb-3">{zh ? "AI 评分 ≥ 75 的通话，沉淀可复用话术" : "Calls scored ≥ 75 by AI"}</p>
        {(data?.topScripts || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6" data-testid="topscripts-empty">{zh ? "暂无高分通话（真实通话转写评分后出现）" : "No high-scoring calls yet"}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.topScripts.map((t) => (
              <div key={t.id} data-testid={`topscript-${t.id}`} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-1.5">
                  <span className={`inline-flex items-center gap-1 text-sm font-bold ${scoreColor(t.score)}`}><Star className="h-3.5 w-3.5 fill-current" />{t.score}</span>
                  <span className="text-xs text-slate-500">{t.agentName}</span>
                  {t.intentTag && <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">{t.intentTag}</span>}
                  {t.leadId && <button onClick={() => navigate(`/crm/leads/${t.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-amber-700 hover:underline inline-flex items-center gap-0.5 text-xs">{t.name || (zh ? "客户" : "Lead")}<ExternalLink className="h-3 w-3" /></button>}
                </div>
                {t.highlight && <p className="text-sm text-slate-800 border-l-2 border-amber-400 pl-2 mb-1">“{t.highlight}”</p>}
                {t.summary && <p className="text-xs text-slate-500">{t.summary}</p>}
                {pinned.canManage && t.highlight && (
                  <div className="mt-1.5 flex">
                    {pinned.ids.includes(t.id)
                      ? <span data-testid={`topscript-pinned-${t.id}`} className="inline-flex items-center gap-1 text-xs text-emerald-600"><BookmarkCheck className="h-3.5 w-3.5" />{zh ? "已入库" : "In library"}</span>
                      : <button data-testid={`topscript-pin-${t.id}`} onClick={() => pin(t)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 border border-amber-200 rounded px-2 py-1"><BookmarkPlus className="h-3.5 w-3.5" />{zh ? "收藏入库" : "Pin"}</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
